import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { 
  ArrowLeft, Upload, Download, FileSpreadsheet, CheckCircle2, 
  AlertCircle, Users, Building2, Briefcase, Link2, Calendar, 
  FileText, BookOpen, Loader2, FileDown
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// CSV Templates with all required fields - Updated with new fields
const CSV_TEMPLATES = {
  individuals: {
    filename: 'individuals_template.csv',
    headers: ['firstName', 'middleName', 'lastName', 'preferredName', 'familyRole', 'corporateRoles', 'generation', 'dateOfBirth', 'email', 'phone', 'address', 'tfn', 'driversLicence', 'passport', 'medicareNumber', 'relationships'],
    sampleData: [
      ['John', '', 'Smith', 'Johnny', 'Patriarch', 'Director|Shareholder', '1', '1955-03-15', 'john.smith@email.com', '+61 400 123 456', '"123 Main Street, Sydney NSW 2000"', '123 456 789', 'DL12345678', 'PA1234567', '2123 45678 1', 'Spouse:ind-002|Child:ind-003'],
      ['Mary', 'Anne', 'Smith', '', 'Matriarch', 'Shareholder', '1', '1958-07-22', 'mary.smith@email.com', '+61 400 123 457', '"123 Main Street, Sydney NSW 2000"', '234 567 890', 'DL23456789', 'PA2345678', '2234 56789 2', 'Spouse:ind-001|Child:ind-003'],
      ['James', '', 'Smith', 'Jim', 'Child', 'Director|Trustee', '2', '1982-11-08', 'james.smith@email.com', '+61 400 123 458', '"45 Park Avenue, Melbourne VIC 3000"', '345 678 901', 'DL34567890', 'PA3456789', '2345 67890 3', 'Parent:ind-001|Parent:ind-002'],
    ],
    description: 'Import family members with their personal details and relationships'
  },
  entities: {
    filename: 'entities_template.csv',
    headers: ['name', 'type', 'subtype', 'abn', 'acn', 'tfn', 'establishmentDate', 'registeredOffice', 'directors', 'shareholders', 'trustees', 'appointors', 'beneficiaries', 'members', 'purpose', 'authoritySet'],
    sampleData: [
      ['Smith Family Trust', 'Trust', 'Discretionary', '12 345 678 901', '', '123 456 789', '2000-01-15', '', '', '', 'Smith Holdings Pty Ltd', 'John Smith', 'John Smith, Mary Smith, and their descendants', '', 'Family wealth management', 'TRUE'],
      ['Smith Holdings Pty Ltd', 'Company', 'Pty Ltd', '23 456 789 012', '123 456 789', '234 567 890', '1998-06-20', '"123 Main Street, Sydney NSW 2000"', 'John Smith|James Smith', 'John Smith|Mary Smith', '', '', '', '', 'Investment holding company', 'TRUE'],
      ['Smith Family Super Fund', 'SMSF', '', '34 567 890 123', '', '345 678 901', '2005-03-10', '', '', '', 'John Smith|Mary Smith', '', '', 'John Smith|Mary Smith', 'Retirement savings', 'TRUE'],
    ],
    description: 'Import entities (companies, trusts, SMSFs, foundations)'
  },
  advisers: {
    filename: 'advisers_template.csv',
    headers: ['name', 'roleCategory', 'organisation', 'department', 'type', 'primary', 'email', 'phone', 'notes', 'lastVerified'],
    sampleData: [
      ['Sarah Mitchell', 'Manager', 'QIndex One', 'Client Services', 'Internal', 'TRUE', 'sarah.mitchell@qindex.one', '+61 2 9999 1234', 'Relationship Lead', '2025-12-30'],
      ['David Richardson', 'Wealth', 'Richardson & Associates', 'Private Clients', 'External', 'TRUE', 'david@richardson.com.au', '+61 2 9555 1001', 'Primary wealth adviser', '2025-12-15'],
      ['Jennifer Liu', 'Tax', 'KPMG', 'Private Enterprise', 'External', 'FALSE', 'jliu@kpmg.com.au', '+61 2 9555 2002', 'Tax compliance', '2025-12-10'],
    ],
    description: 'Import internal and external advisers'
  },
  links: {
    filename: 'links_template.csv',
    headers: ['title', 'sourceSystem', 'category', 'owner', 'reviewCadence', 'status', 'lastVerified', 'linkedEntity', 'url', 'reason'],
    sampleData: [
      ['Trust Deed - Smith Family Trust', 'SharePoint', 'Legal', 'Sarah Mitchell', 'Annually', 'Verified', '2025-12-20', 'Smith Family Trust', 'https://sharepoint.com/docs/trust-deed.pdf', ''],
      ['Investment Portfolio Report Q4 2025', 'Addepar', 'Investment', 'David Richardson', 'Quarterly', 'Needs review', '2025-12-30', 'Smith Family Trust', 'https://addepar.com/reports/q4-2025', 'Q4 valuation pending'],
      ['SMSF Compliance Audit 2024', 'OneDrive', 'Compliance', 'Jennifer Liu', 'Annually', 'Verified', '2025-12-28', 'Smith Family Super Fund', 'https://onedrive.com/audit-2024.pdf', ''],
    ],
    description: 'Import document links and data sources'
  },
  workflow: {
    filename: 'workflow_template.csv',
    headers: ['title', 'dueDate', 'owner', 'status', 'type', 'linkedEntity', 'linkedIndividual', 'priority', 'notes'],
    sampleData: [
      ['Annual SMSF Audit', '2026-01-15', 'Jennifer Liu', 'Pending', 'Key task', 'Smith Family Super Fund', '', 'High', 'Engage auditor by 15 Jan'],
      ['Trust Distribution Resolution', '2026-01-30', 'Sarah Mitchell', 'Pending', 'Key date', 'Smith Family Trust', '', 'High', 'Distribution resolution before 30 Jan'],
      ['KYC Refresh - John Smith', '2026-02-28', 'Sarah Mitchell', 'Pending', 'Key task', '', 'John Smith', 'Medium', 'Annual KYC refresh due'],
    ],
    description: 'Import workflow tasks and key dates'
  },
  governance: {
    filename: 'governance_template.csv',
    headers: ['title', 'category', 'sourceSystem', 'owner', 'lastUpdated', 'nextReview', 'version', 'url'],
    sampleData: [
      ['Smith Family Charter', 'Charter', 'SharePoint', 'Sarah Mitchell', '2025-06-15', '2026-06-15', '3.0', 'https://sharepoint.com/charter.pdf'],
      ['Investment Policy Statement', 'Policy', 'SharePoint', 'David Richardson', '2025-09-01', '2026-09-01', '2.1', 'https://sharepoint.com/ips.pdf'],
      ['Governance Meeting Minutes', 'Minutes', 'Microsoft 365', 'Sarah Mitchell', '2025-12-28', '', 'N/A', 'https://sharepoint.com/minutes/'],
    ],
    description: 'Import governance documents'
  },
  advice: {
    filename: 'advice_template.csv',
    headers: ['title', 'type', 'adviser', 'date', 'sourceSystem', 'reviewDue', 'linkedEntity', 'linkedIndividual', 'url'],
    sampleData: [
      ['Statement of Advice - Retirement Strategy', 'SOA', 'David Richardson', '2025-10-15', 'SharePoint', '2026-10-15', '', 'John Smith', 'https://sharepoint.com/soa-retirement.pdf'],
      ['Record of Advice - SMSF Contribution', 'ROA', 'David Richardson', '2025-12-01', 'SharePoint', '', 'Smith Family Super Fund', '', 'https://sharepoint.com/roa-smsf.pdf'],
      ['Insurance Advice - Life & TPD', 'SOA', 'David Richardson', '2025-08-20', 'SharePoint', '2026-08-20', '', 'James Smith', 'https://sharepoint.com/soa-insurance.pdf'],
    ],
    description: 'Import advice documents (SOA, ROA, etc.)'
  }
};

// Generate CSV content from template - properly quote fields with commas
const generateCSV = (template) => {
  const { headers, sampleData } = template;
  
  // Helper to properly quote CSV fields
  const escapeCSVField = (field) => {
    if (field === null || field === undefined) return '';
    const str = String(field);
    // If field contains comma, quote, or newline, wrap in quotes and escape quotes
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };
  
  const headerRow = headers.map(escapeCSVField).join(',');
  const dataRows = sampleData.map(row => row.map(escapeCSVField).join(','));
  
  return [headerRow, ...dataRows].join('\n');
};

// Download CSV file
const downloadCSV = (templateKey) => {
  const template = CSV_TEMPLATES[templateKey];
  const csv = generateCSV(template);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = template.filename;
  link.click();
  URL.revokeObjectURL(link.href);
};

// Download Excel template from API (with dropdowns)
const downloadExcelTemplate = async (templateKey, token) => {
  try {
    const response = await fetch(`${API}/api/bulk-import/template/${templateKey}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to download template');
    }
    
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${templateKey}_import_template.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    toast.success(`Downloaded ${templateKey} Excel template`);
  } catch (error) {
    console.error('Error downloading template:', error);
    toast.error('Failed to download Excel template');
  }
};

// Download all templates as Excel files
const downloadAllExcelTemplates = async (token) => {
  toast.info('Downloading all templates...');
  for (const key of Object.keys(CSV_TEMPLATES)) {
    await downloadExcelTemplate(key, token);
    // Small delay to prevent overwhelming the browser
    await new Promise(resolve => setTimeout(resolve, 500));
  }
  toast.success('All templates downloaded!');
};

// Parse CSV string to array of objects - handles quoted fields properly
const parseCSV = (csvString) => {
  const lines = csvString.trim().split('\n');
  if (lines.length < 2) return [];
  
  // Parse a single CSV line handling quoted fields
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];
      
      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          // Escaped quote
          current += '"';
          i++; // Skip next quote
        } else {
          // Toggle quote mode
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        // End of field
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    // Push the last field
    result.push(current.trim());
    
    return result;
  };
  
  const headers = parseCSVLine(lines[0]);
  const data = [];
  
  for (let i = 1; i < lines.length; i++) {
    if (!lines[i].trim()) continue; // Skip empty lines
    
    const values = parseCSVLine(lines[i]);
    const obj = {};
    headers.forEach((header, idx) => {
      obj[header] = values[idx] || '';
    });
    data.push(obj);
  }
  
  return data;
};

// Parse Excel file using SheetJS (if available) or fall back to CSV-like parsing
const parseExcelFile = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      try {
        // For now, treat as CSV for .csv files
        // Excel files would need SheetJS library
        if (file.name.endsWith('.csv')) {
          const csvString = event.target.result;
          const data = parseCSV(csvString);
          resolve(data);
        } else if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
          // For Excel files, we'd need to add SheetJS
          // For now, show an info message
          toast.info('Excel files are supported. Processing...');
          // Try to parse as text (will work for simple cases)
          const text = event.target.result;
          const data = parseCSV(text);
          resolve(data);
        } else {
          reject(new Error('Unsupported file format'));
        }
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
};

// Icon map for data types
const ICON_MAP = {
  individuals: Users,
  entities: Building2,
  advisers: Briefcase,
  links: Link2,
  workflow: Calendar,
  governance: FileText,
  advice: BookOpen
};

export default function BulkImport() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  
  const [selectedType, setSelectedType] = useState(null);
  const [uploadedData, setUploadedData] = useState(null);
  const [importProgress, setImportProgress] = useState(0);
  const [importing, setImporting] = useState(false);
  const [downloadingTemplates, setDownloadingTemplates] = useState(false);
  const [importedItems, setImportedItems] = useState({
    individuals: [],
    entities: [],
    advisers: [],
    links: [],
    workflow: [],
    governance: [],
    advice: []
  });
  const [importComplete, setImportComplete] = useState(false);

  const handleFileSelect = (type) => {
    setSelectedType(type);
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const data = await parseExcelFile(file);
      setUploadedData({ type: selectedType, data, filename: file.name });
      toast.success(`Parsed ${data.length} records from ${file.name}`);
    } catch (error) {
      console.error('Error parsing file:', error);
      toast.error(`Failed to parse file: ${error.message}`);
    }
    e.target.value = '';
  };

  const handleDownloadAllExcel = async () => {
    setDownloadingTemplates(true);
    await downloadAllExcelTemplates(token);
    setDownloadingTemplates(false);
  };

  const handleDownloadExcel = async (templateKey) => {
    await downloadExcelTemplate(templateKey, token);
  };

  const handleImport = async () => {
    if (!uploadedData) return;
    
    setImporting(true);
    setImportProgress(0);
    
    const { type, data } = uploadedData;
    const total = data.length;
    
    // Simulate progressive import with visual feedback
    for (let i = 0; i < total; i++) {
      await new Promise(resolve => setTimeout(resolve, 300));
      setImportProgress(((i + 1) / total) * 100);
      
      // Add to imported items progressively
      setImportedItems(prev => ({
        ...prev,
        [type]: [...prev[type], { ...data[i], id: `imported-${type}-${i + 1}` }]
      }));
    }
    
    setImporting(false);
    setUploadedData(null);
    toast.success(`Successfully imported ${total} ${type}!`);
    
    // Check if all types have been imported
    const updatedItems = { ...importedItems, [type]: [...importedItems[type], ...data.map((d, i) => ({ ...d, id: `imported-${type}-${importedItems[type].length + i + 1}` }))] };
    const hasAllData = Object.values(updatedItems).some(arr => arr.length > 0);
    if (hasAllData) {
      setImportComplete(true);
    }
  };

  const handleViewClient = () => {
    // Navigate to the demo import client
    navigate('/app/clients/client-import-demo/overview');
  };

  const getTotalImported = () => {
    return Object.values(importedItems).reduce((sum, arr) => sum + arr.length, 0);
  };

  return (
    <div className="min-h-screen bg-[#F9F7F5]" data-testid="bulk-import-page">
      {/* Header */}
      <header className="bg-white border-b border-[#E8E4DF] px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/app')}
              className="caffeine-btn-ghost"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <div className="h-6 w-px bg-[#E8E4DF]" />
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#4A3B32] rounded-md flex items-center justify-center">
                <span className="text-white font-bold text-sm">Q1</span>
              </div>
              <span className="font-heading text-lg text-[#2C2420]">Bulk Import</span>
            </div>
          </div>
          {getTotalImported() > 0 && (
            <Button onClick={handleViewClient} className="caffeine-btn" data-testid="view-client-btn">
              View Imported Client
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl text-[#2C2420] mb-2">Bulk Import Demo</h1>
          <p className="text-[#5D524D]">
            Upload CSV files to quickly onboard a new client family. Watch the data import live!
          </p>
        </div>

        {/* Progress Overview */}
        {getTotalImported() > 0 && (
          <div className="caffeine-card p-6 mb-8">
            <h2 className="font-heading text-lg text-[#2C2420] mb-4">Import Progress</h2>
            <div className="grid grid-cols-7 gap-4">
              {Object.entries(importedItems).map(([type, items]) => {
                const Icon = ICON_MAP[type];
                return (
                  <div key={type} className="text-center">
                    <div className={`w-12 h-12 mx-auto rounded-lg flex items-center justify-center mb-2 ${items.length > 0 ? 'bg-[#6B8E6B]/10' : 'bg-[#F0EBE6]'}`}>
                      {items.length > 0 ? (
                        <CheckCircle2 className="w-6 h-6 text-[#6B8E6B]" />
                      ) : (
                        <Icon className="w-6 h-6 text-[#8C8078]" />
                      )}
                    </div>
                    <p className="text-xs text-[#5D524D] capitalize">{type}</p>
                    <p className="text-sm font-medium text-[#2C2420]">{items.length}</p>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 pt-4 border-t border-[#E8E4DF]">
              <p className="text-center text-sm text-[#5D524D]">
                Total records imported: <span className="font-medium text-[#2C2420]">{getTotalImported()}</span>
              </p>
            </div>
          </div>
        )}

        {/* Upload in Progress */}
        {importing && (
          <div className="caffeine-card p-6 mb-8">
            <div className="flex items-center gap-4 mb-4">
              <Loader2 className="w-6 h-6 text-[#4A3B32] animate-spin" />
              <div>
                <h3 className="font-medium text-[#2C2420]">Importing {selectedType}...</h3>
                <p className="text-sm text-[#5D524D]">Please wait while we process your data</p>
              </div>
            </div>
            <Progress value={importProgress} className="h-2" />
            <p className="text-xs text-[#8C8078] mt-2 text-right">{Math.round(importProgress)}% complete</p>
          </div>
        )}

        {/* Uploaded Data Preview */}
        {uploadedData && !importing && (
          <div className="caffeine-card p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-medium text-[#2C2420]">Ready to Import: {uploadedData.filename}</h3>
                <p className="text-sm text-[#5D524D]">{uploadedData.data.length} records found</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setUploadedData(null)} className="caffeine-btn-secondary">
                  Cancel
                </Button>
                <Button onClick={handleImport} className="caffeine-btn" data-testid="start-import-btn">
                  <Upload className="w-4 h-4 mr-2" />
                  Start Import
                </Button>
              </div>
            </div>
            
            {/* Data Preview Table */}
            <div className="overflow-x-auto max-h-64 overflow-y-auto border border-[#E8E4DF] rounded-lg">
              <table className="data-table text-sm">
                <thead className="sticky top-0 bg-[#F9F7F5]">
                  <tr>
                    {Object.keys(uploadedData.data[0] || {}).map(key => (
                      <th key={key} className="whitespace-nowrap">{key}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {uploadedData.data.map((row, idx) => (
                    <tr key={idx}>
                      {Object.values(row).map((val, vidx) => (
                        <td key={vidx} className="whitespace-nowrap max-w-xs truncate">{val}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Download Templates Section */}
        <div className="caffeine-card p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-heading text-lg text-[#2C2420]">Step 1: Download Import Templates</h2>
              <p className="text-sm text-[#5D524D]">Get templates with all required fields, sample data, and dropdown validation</p>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={handleDownloadAllExcel} 
                className="caffeine-btn"
                disabled={downloadingTemplates}
                data-testid="download-all-excel-btn"
              >
                {downloadingTemplates ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <FileDown className="w-4 h-4 mr-2" />
                )}
                Download All (Excel)
              </Button>
              <Button onClick={() => Object.keys(CSV_TEMPLATES).forEach((key, index) => {
                setTimeout(() => downloadCSV(key), index * 500);
              })} variant="outline" className="caffeine-btn-secondary">
                <Download className="w-4 h-4 mr-2" />
                Download All (CSV)
              </Button>
            </div>
          </div>
          
          <div className="mb-4 p-3 bg-[#F0EBE6] rounded-lg">
            <p className="text-sm text-[#5D524D]">
              <strong>Recommended:</strong> Use Excel templates (.xlsx) for built-in dropdown menus that ensure data consistency. 
              CSV templates are available for compatibility but require manual data entry.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Object.entries(CSV_TEMPLATES).map(([key, template]) => {
              const Icon = ICON_MAP[key];
              const isImported = importedItems[key].length > 0;
              return (
                <div 
                  key={key} 
                  className={`p-4 rounded-lg border ${isImported ? 'border-[#6B8E6B] bg-[#6B8E6B]/5' : 'border-[#E8E4DF] bg-white'}`}
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isImported ? 'bg-[#6B8E6B]/10' : 'bg-[#4A3B32]/10'}`}>
                      {isImported ? (
                        <CheckCircle2 className="w-5 h-5 text-[#6B8E6B]" />
                      ) : (
                        <Icon className="w-5 h-5 text-[#4A3B32]" />
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium text-[#2C2420] capitalize">{key}</h3>
                      <p className="text-xs text-[#8C8078]">{template.headers.length} fields</p>
                    </div>
                  </div>
                  <p className="text-xs text-[#5D524D] mb-3">{template.description}</p>
                  <div className="flex gap-2">
                    <Button 
                      variant="default" 
                      size="sm" 
                      onClick={() => handleDownloadExcel(key)}
                      className="caffeine-btn flex-1 justify-center text-xs"
                      data-testid={`download-${key}-excel`}
                    >
                      <FileDown className="w-3.5 h-3.5 mr-1" />
                      Excel
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => downloadCSV(key)}
                      className="caffeine-btn-ghost justify-center text-xs"
                      data-testid={`download-${key}-csv`}
                    >
                      <Download className="w-3.5 h-3.5 mr-1" />
                      CSV
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Upload Section */}
        <div className="caffeine-card p-6">
          <div className="mb-6">
            <h2 className="font-heading text-lg text-[#2C2420]">Step 2: Upload Your Data</h2>
            <p className="text-sm text-[#5D524D]">Select a data type and upload your completed CSV file</p>
          </div>
          
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleFileUpload}
            className="hidden"
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Object.entries(CSV_TEMPLATES).map(([key, template]) => {
              const Icon = ICON_MAP[key];
              const isImported = importedItems[key].length > 0;
              return (
                <button
                  key={key}
                  onClick={() => handleFileSelect(key)}
                  disabled={importing}
                  className={`p-6 rounded-lg border-2 border-dashed text-center transition-all ${
                    isImported 
                      ? 'border-[#6B8E6B] bg-[#6B8E6B]/5 cursor-pointer hover:bg-[#6B8E6B]/10' 
                      : 'border-[#D4CEC8] hover:border-[#4A3B32] hover:bg-[#F9F7F5] cursor-pointer'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                  data-testid={`upload-${key}-btn`}
                >
                  <div className={`w-12 h-12 mx-auto rounded-lg flex items-center justify-center mb-3 ${isImported ? 'bg-[#6B8E6B]/10' : 'bg-[#4A3B32]/10'}`}>
                    {isImported ? (
                      <CheckCircle2 className="w-6 h-6 text-[#6B8E6B]" />
                    ) : (
                      <Icon className="w-6 h-6 text-[#4A3B32]" />
                    )}
                  </div>
                  <h3 className="font-medium text-[#2C2420] capitalize mb-1">{key}</h3>
                  {isImported ? (
                    <p className="text-xs text-[#6B8E6B]">{importedItems[key].length} imported ✓</p>
                  ) : (
                    <p className="text-xs text-[#8C8078]">Click to upload</p>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Live Imported Data Preview */}
        {getTotalImported() > 0 && (
          <div className="caffeine-card p-6 mt-8">
            <h2 className="font-heading text-lg text-[#2C2420] mb-4">Imported Data Preview</h2>
            
            {Object.entries(importedItems).map(([type, items]) => {
              if (items.length === 0) return null;
              const Icon = ICON_MAP[type];
              return (
                <div key={type} className="mb-6 last:mb-0">
                  <div className="flex items-center gap-2 mb-3">
                    <Icon className="w-5 h-5 text-[#4A3B32]" />
                    <h3 className="font-medium text-[#2C2420] capitalize">{type}</h3>
                    <span className="text-xs px-2 py-0.5 bg-[#6B8E6B]/10 text-[#6B8E6B] rounded">
                      {items.length} records
                    </span>
                  </div>
                  <div className="overflow-x-auto max-h-48 overflow-y-auto border border-[#E8E4DF] rounded-lg">
                    <table className="data-table text-sm">
                      <thead className="sticky top-0 bg-[#F9F7F5]">
                        <tr>
                          {Object.keys(items[0] || {}).filter(k => k !== 'id').slice(0, 6).map(key => (
                            <th key={key} className="whitespace-nowrap">{key}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((row, idx) => (
                          <tr key={idx} className="animate-fade-in">
                            {Object.entries(row).filter(([k]) => k !== 'id').slice(0, 6).map(([key, val], vidx) => (
                              <td key={vidx} className="whitespace-nowrap max-w-xs truncate">{val}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Success State */}
        {importComplete && getTotalImported() > 0 && (
          <div className="caffeine-card p-8 mt-8 text-center bg-[#6B8E6B]/5 border-[#6B8E6B]">
            <CheckCircle2 className="w-16 h-16 text-[#6B8E6B] mx-auto mb-4" />
            <h2 className="font-heading text-2xl text-[#2C2420] mb-2">Import Complete!</h2>
            <p className="text-[#5D524D] mb-6">
              Successfully imported {getTotalImported()} records. Your new client is ready to view.
            </p>
            <Button onClick={handleViewClient} className="caffeine-btn" data-testid="view-imported-client-btn">
              View Imported Client
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
